import { Button, CancelButton, FormAutocomplete, FormAutocompleteMulti, FormText, TooltipShortcut } from "@itk/mui-components";
import { Skeleton, Stack } from "@mui/material";
import { useNavigate } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { minCount, OptionalFormController, OptionalFormInclude, required, useForm, useGridForm } from "@itk/react-form";
import { useEffect, useMemo, useState } from "react";
import { GenericDialog } from "src/components/generic-dialog";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Model_AttributeDetailsGet, Model_ProjectExtendedGet, Shared_AttributeOwnerEntityKinds, Shared_ProjectVisibilitySettings } from "src/backend/openapi/v1";
import { createGuid, hasValue } from "@itk/typescript-utils";
import { projectNameValidation } from "./add-edit-project-dialog-utils";
import { addEditProjectDialogActions } from "./add-edit-project-dialog-store";
import { useProjectsPageAddEditProjectDialogT } from "src/translation-hooks/projects-page-add-edit-project-dialog";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { useGetCustomerId } from "src/hooks";
import { AddEditProjectDialogFormType, DialogType } from "./add-edit-project-dialog-types";
import { applicationActions } from "src/components/application-store";
import { AttributeRenderer, SaveAndCancelButton } from "src/components";
import { projectListActions } from "../../project-list-store";
import { projectsPageK, useProjectsPageT } from "src/translation-hooks/projects-page";

const mapStateToProps = (state: RootState) => ({
  projectsAttributes: state.pageReducer.projectListReducer.projectsAttributes,
  visibilityContactPersons: state.pageReducer.addEditProjectDialogReducer.visibilityContactPersons
});

const ownActions = {
  createModifyProjectRequest: addEditProjectDialogActions.createModifyProjectRequest,
  getProjectrequest: addEditProjectDialogActions.getProjectrequest,
  getVisibilityContactPersonsRequest: addEditProjectDialogActions.getVisibilityContactPersonsRequest,
  getProjectsAttributesRequest: projectListActions.getProjectsAttributesRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    setOpen: (isOpen: boolean) => void;
    projectId?: string;
    variant: DialogType;
  };

const addEditProjectDialogBase = (props: Props) => {
  const {
    projectId,
    setOpen,
    projectsAttributes,
    visibilityContactPersons,
    createModifyProjectRequest,
    getProjectrequest,
    getVisibilityContactPersonsRequest,
    setWarningAction,
    getProjectsAttributesRequest,
    variant
  } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const projectsPageT = useProjectsPageT();
  const projectsPageAddEditProjectDialogT = useProjectsPageAddEditProjectDialogT();
  const validationMessageTranslationsT = useValidationMessageTranslationsT();

  const navigate = useNavigate();
  const { customerId } = useGetCustomerId();
  const [isLoading, setIsLoading] = useState(false);
  const [isGetProjectDetailsLoading, setIsGetProjectLoading] = useState(false);
  const [projectDetails, setProjectDetails] = useState<Model_ProjectExtendedGet>();

  useEffect(() => {
    if (projectId && customerId) {
      setIsGetProjectLoading(true);
      getProjectrequest(projectId, (isSuccess, data) => {
        if (isSuccess && data) {
          setProjectDetails(data);
        }
        setIsGetProjectLoading(false);
      });
    }
  }, [customerId]);

  // Get project attributes
  useEffect(() => {
    if (customerId) {
      getProjectsAttributesRequest();
    }
  }, [customerId]);

  // form
  const initialData = useMemo((): AddEditProjectDialogFormType => {
    return {
      description: projectDetails?.description ?? "",
      customerId,
      projectVisibilitySettingId: projectDetails?.projectVisibilitySettingId ?? Shared_ProjectVisibilitySettings.Public,
      contactPersonIds: projectDetails?.contactPersons ?? []
    };
  }, [projectDetails]);

  const initController = useMemo(
    (): OptionalFormController<AddEditProjectDialogFormType> => ({
      description: {
        validators: [required, projectNameValidation(validationMessageTranslationsT)]
      },
      projectVisibilitySettingId: {
        validators: [required]
      },

      contactPersonIds: {
        validators: state => (state.projectVisibilitySettingId.value === Shared_ProjectVisibilitySettings.ContactPerson ? [minCount(1, validationMessageTranslationsT.mustHaveAtleast1user)] : [])
      }
    }),
    []
  );

  const formInclude = useMemo((): OptionalFormInclude<AddEditProjectDialogFormType> => ({ description: true, contactPersonIds: true, projectVisibilitySettingId: true }), []);

  const { formState, formController, validate, getValues, hasChanges } = useForm(initialData, initController, formInclude);

  // attribute
  const attributes = useMemo(() => {
    const attributes = projectId ? projectDetails?.attributes : projectsAttributes.loading === "has-loaded" && projectsAttributes.data;
    return attributes || [];
  }, [projectDetails?.attributes, projectsAttributes.loading]);

  const attributeColumns = useMemo(() => {
    return attributes && attributes?.length > 0 ? Object.keys(attributes[0]).map(key => ({ field: key })) : [];
  }, [attributes]);

  const attributeInclude = useMemo((): OptionalFormInclude<Model_AttributeDetailsGet> => {
    if (attributes && attributes.length > 0)
      return Object.keys(attributes[0]).reduce(
        (acc, x) => {
          const key = x as keyof Model_AttributeDetailsGet;
          acc[key] = true;
          return acc;
        },
        {} as Record<keyof Model_AttributeDetailsGet, true>
      );
    return {};
  }, [attributes]);

  const attributeController = useMemo((): OptionalFormController<Model_AttributeDetailsGet> => {
    if (attributes && attributes?.length > 0)
      return {
        value: {
          validators: data => {
            if (data.isRequired.value) return [required];
            if (!data.isValid.value) return [_ => validationMessageTranslationsT.invalidValue];
            return [];
          }
        }
      };
    return {};
  }, [attributes, validationMessageTranslationsT]);

  const {
    rows: attributeRows,
    getValues: attributesGetValues,
    validate: attributesValidate,
    hasChanges: attributesHasChanges
  } = useGridForm<Model_AttributeDetailsGet, (typeof attributeColumns)[0], keyof Model_AttributeDetailsGet>(attributes, attributeColumns, "attributeId", attributeController, attributeInclude);

  useEffect(() => {
    if (customerId && formState.projectVisibilitySettingId.value === Shared_ProjectVisibilitySettings.ContactPerson) {
      getVisibilityContactPersonsRequest();
    }
  }, [formState.projectVisibilitySettingId.value, customerId]);

  const visilityOptions = [
    {
      label: projectsPageT.public,
      value: Shared_ProjectVisibilitySettings.Public
    },
    {
      label: projectsPageT.private,
      value: Shared_ProjectVisibilitySettings.Private
    },
    {
      label: projectsPageT.users,
      value: Shared_ProjectVisibilitySettings.ContactPerson
    }
  ];

  const handleCreateModifyProject = () => {
    const errors = validate();
    const attributeErrors = attributesValidate("all");
    if (hasValue(errors) || hasValue(attributeErrors)) return;

    const values = getValues();
    const attributeValues = attributesGetValues("all");

    if (!customerId) return;

    setIsLoading(true);
    createModifyProjectRequest(
      {
        ...values,
        // attribute.value is now an array with one element per dimension part; project attributes are
        // always scalar, so pull out the single part === null element instead of stringifying the array
        attributes: attributeValues?.map(attribute => ({
          attributeId: attribute.attributeId,
          value: attribute.value?.find(v => v.part === null)?.value ?? "",
          attributeEntityKind: Shared_AttributeOwnerEntityKinds.Project
        })),
        projectId: !projectId ? createGuid() : projectId,
        customerId
      },
      (isSuccess, newProjectId) => {
        if (isSuccess) {
          // if projectId wasn't present before api call that means it was create project hence navigate
          // else close popup because project was edited
          // TODO
          if (!projectId) {
            newProjectId && navigate(ROUTES_URL.CONFIGURATOR.build(newProjectId));
          } else {
            setOpen(false);
          }
        }
        setIsLoading(false);
      }
    );
  };

  const onClose = () => {
    if (hasChanges || attributesHasChanges) {
      setWarningAction({
        confirm: () => {
          setOpen(false);
          setWarningAction(undefined);
        },
        dialogProps: {
          message: genericTranslationsT.unsavedChanges
        },
        variant: "confirmCancel"
      });
    } else {
      setOpen(false);
    }
  };

  return (
    <GenericDialog
      dialogProps={{
        disableEnforceFocus: true,
        disableRestoreFocus: true
      }}
      isLoading={isLoading}
      headerTitle={projectId ? projectsPageAddEditProjectDialogT.editProject : projectsPageAddEditProjectDialogT.createProject}
      renderDialogActions={
        variant === "create" ? (
          <>
            <CancelButton onCancel={onClose} disabled={isLoading} variant="outlined" />
            <TooltipShortcut shortcut={{ action: handleCreateModifyProject, shortCutKey: "g", modifier: "alt" }}>
              <Button
                t={genericTranslationsT}
                k={genericTranslationsK}
                field="dynamicValue"
                onClick={handleCreateModifyProject}
                variant="contained"
                disabled={!hasChanges && !attributesHasChanges}
                tArgs={[projectId ? genericTranslationsT.save : projectsPageAddEditProjectDialogT.createProject]}
                loading={isLoading}
              />
            </TooltipShortcut>
          </>
        ) : (
          <SaveAndCancelButton onCancel={onClose} onSave={handleCreateModifyProject} />
        )
      }
      onClose={onClose}
    >
      {(projectsAttributes.loading === "is-loading" || isGetProjectDetailsLoading) && (
        <Stack justifyContent="center" alignItems="center" gap={2} my={2}>
          <Skeleton width="100%" variant="rectangular" height={40} />
          <Skeleton width="100%" variant="rectangular" height={40} />
          <Skeleton width="100%" variant="rectangular" height={40} />
        </Stack>
      )}

      {projectsAttributes.loading === "has-loaded" && !isGetProjectDetailsLoading && (
        <Stack direction="column" gap={2} my={1}>
          <FormText
            autoFocus
            t={projectsPageT}
            k={projectsPageK}
            field="projectName"
            formFieldState={formState.description}
            formFieldController={formController.description}
            width="100%"
            slotProps={{ htmlInput: { maxLength: 200 } }}
            helperText={`${formState.description.value?.length ?? 0}/200`}
          />
          {attributeRows.length > 0 &&
            attributeRows.map(attributeFormItem => {
              return <AttributeRenderer key={attributeFormItem.formState.attributeId.value} formItem={attributeFormItem} />;
            })}
          <FormAutocomplete
            t={projectsPageT}
            k={projectsPageK}
            field="visibility"
            formFieldState={formState.projectVisibilitySettingId}
            formFieldController={formController.projectVisibilitySettingId}
            options={visilityOptions.sort((x, y) => x.label.localeCompare(y.label))}
            getId={x => x.value}
            getOptionLabel={x => x.label}
            width="100%"
          />
          {formState.projectVisibilitySettingId.value && formState.projectVisibilitySettingId.value === Shared_ProjectVisibilitySettings.ContactPerson && (
            <>
              {visibilityContactPersons.loading === "is-loading" && <Skeleton height={40} />}
              {visibilityContactPersons.loading === "has-loaded" && (
                <FormAutocompleteMulti
                  t={projectsPageT}
                  k={projectsPageK}
                  field="users"
                  options={visibilityContactPersons.data}
                  formFieldState={formState.contactPersonIds}
                  formFieldController={formController.contactPersonIds}
                  getId={x => x.contactPersonId}
                  getOptionLabel={x => x.fullName}
                  width="100%"
                />
              )}
            </>
          )}
        </Stack>
      )}
    </GenericDialog>
  );
};

export const AddEditProjectDialog = connect(mapStateToProps, ownActions)(addEditProjectDialogBase);
